import { useState } from "react";
import { 
  EnvelopeSimple, 
  Phone, 
  MapPin, 
  PaperPlaneTilt,
  CircleNotch,
  CheckCircle,
  ArrowLeft
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useNavigate } from "react-router-dom";
import React, { useEffect } from "react";

export default function ContactPage({ onSubmit }) {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState({
    name: "",
    mobile_number: "",
    email_id: "",
    subject: "",
    message: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const subjects = [
    "General Inquiry",
    "Material Order Support",
    "Logistics Issue",
    "Payment & Billing",
    "Partnership Inquiry",
    "Technical Support",
    "Feedback & Suggestions",
    "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await onSubmit(formData);
    setIsLoading(false);
    if (result.success) {
      setIsSuccess(true);
      setFormData({ name: "", mobile_number: "", email_id: "", subject: "", message: "" });
    }
  };

  const contactInfo = [
    {
      icon: EnvelopeSimple,
      label: "Email Us",
      value: "support@loado.in",
      href: "mailto:support@loado.in"
    },
    {
      icon: Phone,
      label: "Call Us",
      value: "+91 9429694731",
      href: "tel:+919429694731"
    },
    {
      icon: MapPin,
      label: "Available In",
      value: "Chennai",
      href: "#"
    }
  ];

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-[#F8FAFC]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          data-testid="back-to-home"
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={20} weight="bold" />
          <span className="font-medium">Back to Home</span>
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Info */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <EnvelopeSimple size={20} weight="duotone" className="text-[#0055FF]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0055FF]">
                  Contact Us
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0F172A] mb-4">
                Get in Touch
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Have questions or need assistance? Our support team is here to help 
                you. Reach out and we'll respond as quickly as possible.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <a
                  key={index}
                  href={info.href}
                  data-testid={`contact-info-${index}`}
                  className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-lg hover:border-[#0055FF] transition-colors"
                >
                  <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center rounded-lg">
                    <info.icon size={24} weight="duotone" className="text-[#0055FF]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{info.label}</p>
                    <p className="font-semibold text-[#0F172A]">{info.value}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Business Hours */}
            <div className="p-6 bg-white border border-slate-200 rounded-lg">
              <h3 className="font-bold text-[#0F172A] mb-4">Business Hours</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Monday - Saturday</span>
                  <span className="font-medium text-[#0F172A]">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Sunday</span>
                  <span className="font-medium text-[#0F172A]">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-100 mt-2">
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white p-8 lg:p-10 border border-slate-200 rounded-lg">
            {isSuccess ? (
              <div 
                data-testid="contact-success-message"
                className="text-center py-12"
              >
                <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-[#0F172A] mb-2">Message Sent!</h4>
                <p className="text-slate-600 mb-6">
                  We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setIsSuccess(false)}
                  className="text-[#0055FF] font-medium hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name" className="text-sm font-medium text-slate-700">
                      Your Name *
                    </Label>
                    <Input
                      id="contact_name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email" className="text-sm font-medium text-slate-700">
                      Email Address *
                    </Label>
                    <Input
                      id="contact_email"
                      data-testid="contact-email-input"
                      type="email"
                      value={formData.email_id}
                      onChange={(e) => setFormData({ ...formData, email_id: e.target.value })}
                      placeholder="john@example.com"
                      required
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_phone" className="text-sm font-medium text-slate-700">
                      Mobile Number *
                    </Label>
                    <Input
                      id="contact_phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      value={formData.mobile_number}
                      onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                      placeholder="9876543210"
                      required
                      minLength={10}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_subject" className="text-sm font-medium text-slate-700">
                      Subject *
                    </Label>
                    <Select
                      value={formData.subject}
                      onValueChange={(value) => setFormData({ ...formData, subject: value })}
                    >
                      <SelectTrigger 
                        data-testid="contact-subject-select"
                        className="mt-1.5"
                      >
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((subject) => (
                          <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_message" className="text-sm font-medium text-slate-700">
                    Your Message *
                  </Label>
                  <Textarea
                    id="contact_message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    rows={5}
                    required
                    className="mt-1.5 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isLoading || !formData.name || !formData.mobile_number || !formData.email_id || !formData.subject || !formData.message}
                  className="w-full py-3 bg-[#0055FF] text-white font-semibold hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 rounded-lg"
                >
                  {isLoading ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} weight="bold" />
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting, you agree to our Privacy Policy and Terms of Service.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
